import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  User, 
  Calendar, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  XCircle,
  Eye,
  Filter,
  Search,
  Package,
  FileText,
  Receipt,
  FolderPlus,
  Settings,
  ArrowLeft
} from 'lucide-react';
import { purchaseRequestApiService, PurchaseRequest } from '../services/purchaseRequestApi';
import { projectApiService } from '../services/projectApi';

const PurchaseRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [purchaseRequests, setPurchaseRequests] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);

  useEffect(() => {
    loadPurchaseRequests();
  }, []);

  const loadPurchaseRequests = async () => {
    try {
      setLoading(true);
      const requests = await purchaseRequestApiService.getAllPurchaseRequests();
      setPurchaseRequests(requests);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des demandes d\'achat:', err);
      setError('Erreur lors du chargement des demandes d\'achat');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'revision':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'revision':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'pending':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmée';
      case 'revision':
        return 'En révision';
      case 'pending':
        return 'En attente';
      case 'cancelled':
        return 'Annulée';
      default:
        return status;
    }
  };

  const filteredRequests = purchaseRequests.filter(request => {
    const matchesStatus = selectedStatus === 'all' || request.status === selectedStatus;
    const matchesSearch = request.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.client_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatPrice = (price: number | undefined | null) => {
    const safePrice = price || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(safePrice);
  };

  const safeToFixed = (value: any, decimals: number = 2) => {
    const num = Number(value || 0);
    return isNaN(num) ? '0.00' : num.toFixed(decimals);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewInvoice = (request: PurchaseRequest) => {
    // Reconstituer les données pour la facture proforma
    const selectedServices = request.items.map(item => ({
      service: item.service ? {
        id: item.service.toString(),
        name: item.service_name || 'Service',
        price: item.unit_price,
        price_type: 'fixed' as const
      } : undefined,
      pack: item.pack ? {
        id: item.pack.toString(),
        name: item.pack_name || 'Pack',
        total_price: item.unit_price
      } : undefined,
      type: item.type,
      quantity: item.quantity,
      pages: item.pages,
      description: item.description
    }));

    // Naviguer vers la facture proforma avec les données
    navigate('/facture-proforma', {
      state: {
        selectedServices,
        purchaseRequestId: request.id, // 🔧 AJOUT: ID de la facture à réviser
        promoDiscount: request.discount_percentage,
        promoCode: request.promo_code,
        clientInfo: {
          name: request.client_name,
          email: request.client_email,
          phone: request.client_phone,
          company: request.client_company,
          address: request.client_address
        }
      }
    });
  };

  // Fonction pour créer un projet à partir d'une facture confirmée
  const createProject = async (request: PurchaseRequest) => {
    if (request.status !== 'confirmed') {
      alert('Seules les factures confirmées peuvent être converties en projet');
      return;
    }

    const confirmMessage = `Êtes-vous sûr de vouloir créer un projet pour la facture #${request.invoice_id || request.id} ?\n\nClient: ${request.client_name}\nMontant: ${formatPrice(request.total_amount)}\n\nCela créera un nouveau projet dans le système.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      
      console.log('🚀 Création du projet pour la facture:', request.id);
      
      if (!request.id) {
        throw new Error('ID de facture manquant');
      }
      
      const project = await projectApiService.createProjectFromInvoice(request.id as number);
      
      console.log('✅ Projet créé avec succès:', project);
      
      alert(`✅ Projet créé avec succès !\n\nID du projet: ${project.project_id}\nClient: ${project.client_name}\n\nLe client pourra voir ce projet dans sa section /projets`);
      
      // Recharger les données pour mettre à jour l'affichage
      loadPurchaseRequests();
      
    } catch (error) {
      console.error('❌ Erreur lors de la création du projet:', error);
      alert('Une erreur est survenue lors de la création du projet. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des demandes d'achat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadPurchaseRequests}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* En-tête */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Purchase Requests</h1>
                <p className="text-gray-600">Gestion des demandes d'achat</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/gestionprojets')}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Gestion Projets</span>
              </button>
              <div className="bg-blue-50 px-4 py-2 rounded-lg">
                <span className="text-sm font-medium text-blue-700">
                  {filteredRequests.length} demande{filteredRequests.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filtre par statut */}
            <div className="sm:w-48">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="confirmed">Confirmées</option>
                  <option value="revision">En révision</option>
                  <option value="pending">En attente</option>
                  <option value="cancelled">Annulées</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des demandes */}
        <div className="space-y-4">
          {filteredRequests.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune demande trouvée</h3>
              <p className="text-gray-500">
                {searchTerm || selectedStatus !== 'all' 
                  ? 'Aucune demande ne correspond à vos critères de recherche.'
                  : 'Aucune demande d\'achat n\'a encore été créée.'
                }
              </p>
            </div>
          ) : (
            filteredRequests.map((request) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <button
                            onClick={() => navigate(`/detailsdemande/${request.id}`)}
                            className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors text-left"
                          >
                            Facture #{request.invoice_id || request.id}
                          </button>
                          <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(request.status)}`}>
                            <div className="flex items-center space-x-1">
                              {getStatusIcon(request.status)}
                              <span>{getStatusLabel(request.status)}</span>
                            </div>
                          </div>
                          {request.has_variable_pricing && (
                            <div className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                              Prix variables
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>{request.client_name || request.user_name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(request.created_at!)}</span>
                          </div>
                          {request.client_email && (
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-400">✉</span>
                              <span>{request.client_email}</span>
                            </div>
                          )}
                          <div className="flex items-center space-x-2">
                            <Package className="w-4 h-4" />
                            <span>{request.items?.length || 0} item{(request.items?.length || 0) !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {formatPrice(request.total_amount)}
                      </div>
                      {request.discount_percentage && Number(request.discount_percentage) > 0 && (
                        <div className="text-sm text-green-600">
                          -{safeToFixed(request.discount_percentage)}%
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Items preview */}
                  {request.items && request.items.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Items inclus :</h4>
                      <div className="flex flex-wrap gap-2">
                        {request.items.slice(0, 3).map((item, index) => (
                          <div key={index} className="bg-gray-50 px-3 py-1 rounded-full text-sm text-gray-600">
                            {item.service_name || item.pack_name || `${item.type} #${index + 1}`}
                            {item.quantity > 1 && ` x${item.quantity}`}
                          </div>
                        ))}
                        {request.items.length > 3 && (
                          <div className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-500">
                            +{request.items.length - 3} autres
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t mt-4">
                    <div className="text-sm text-gray-500">
                      {request.promo_code && (
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium mr-2">
                          Code: {request.promo_code}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/detailsdemande/${request.id}`)}
                        className="flex items-center space-x-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Détails complets</span>
                      </button>
                      <button
                        onClick={() => viewInvoice(request)}
                        className="flex items-center space-x-2 px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      >
                        <Receipt className="w-4 h-4" />
                        <span>Voir facture</span>
                      </button>
                      <button
                        onClick={() => setSelectedRequest(request)}
                        className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Aperçu</span>
                      </button>
                      {request.status === 'confirmed' && (
                        <button
                          onClick={() => createProject(request)}
                          className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                        >
                          <FolderPlus className="w-4 h-4" />
                          <span>Créer le projet</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Modal de détails */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  Détails de la facture #{selectedRequest.invoice_id || selectedRequest.id}
                </h2>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      viewInvoice(selectedRequest);
                      setSelectedRequest(null);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Receipt className="w-4 h-4" />
                    <span>Voir facture</span>
                  </button>
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              {/* Contenu détaillé de la demande */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Informations client</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Nom:</span> {selectedRequest.client_name}</div>
                    <div><span className="font-medium">Email:</span> {selectedRequest.client_email}</div>
                    <div><span className="font-medium">Téléphone:</span> {selectedRequest.client_phone || 'Non renseigné'}</div>
                    <div><span className="font-medium">Entreprise:</span> {selectedRequest.client_company || 'Non renseigné'}</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Informations financières</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Sous-total:</span> {formatPrice(selectedRequest.subtotal)}</div>
                    <div><span className="font-medium">Réduction:</span> {safeToFixed(selectedRequest.discount_percentage)}% ({formatPrice(selectedRequest.discount_amount)})</div>
                    <div><span className="font-medium">TVA:</span> {safeToFixed(selectedRequest.tva_percentage)}% ({formatPrice(selectedRequest.tva_amount)})</div>
                    <div className="font-semibold"><span>Total:</span> {formatPrice(selectedRequest.total_amount)}</div>
                  </div>
                </div>
              </div>

              {/* Items détaillés */}
              {selectedRequest.items && selectedRequest.items.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Items commandés</h3>
                  <div className="space-y-3">
                    {selectedRequest.items.map((item, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {item.service_name || item.pack_name || `${item.type} #${index + 1}`}
                            </h4>
                            <div className="text-sm text-gray-600 mt-1">
                              <span className="capitalize">{item.type}</span>
                              {item.quantity > 1 && ` • Quantité: ${item.quantity}`}
                              {item.pages && ` • Pages: ${item.pages}`}
                            </div>
                            {item.description && (
                              <p className="text-sm text-gray-600 mt-2">{item.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-gray-900">
                              {formatPrice(item.total_price)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatPrice(item.unit_price || 0)} unitaire
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseRequestsPage;
